/**
 * ScheduleApprovalEntry — deliver an issued schedule version to the client for approval.
 *
 * entry.schedule = { release: { id, milestone_name, issued_date, item_count }, items: [...] } (the
 * frozen snapshot from schedule_releases, resolved server-side by api/journey.js). data: { intro? }.
 * Client actions: approve | request_changes (with a note). On return the recorded decision is shown.
 *
 * The decision is written back to deliverable_approvals by api/journey-respond.js (tied to the
 * project + milestone + this release).
 */

import { useState } from 'react'
import Markdown from '../../components/Markdown.jsx'
import { J, PrimaryButton, GhostButton, DoneChip, ViewOnlyNote } from '../journeyUI.jsx'
import { SCHEDULE_TYPES, scheduleSpecFor, scheduleCell } from '../../services/scheduleLayout.js'

function groupByType(items) {
  const by = {}
  for (const it of (items || [])) (by[it.schedule_type] = by[it.schedule_type] || []).push(it)
  const ordered = SCHEDULE_TYPES.filter(t => by[t]).concat(Object.keys(by).filter(t => !SCHEDULE_TYPES.includes(t)))
  return ordered.map(t => [t, by[t]])
}

function ScheduleSummary({ items }) {
  const groups = groupByType(items)
  if (!groups.length) return <div style={{ ...J.body, fontStyle: 'italic', color: '#7a9aaa' }}>No items in this schedule.</div>
  return (
    <div style={{ marginBottom: 20 }}>
      {groups.map(([type, list]) => {
        const spec = scheduleSpecFor(type)
        return (
          <div key={type} style={{ marginBottom: 16 }}>
            <div style={{ fontFamily: '"Montserrat",sans-serif', fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#07272D', marginBottom: 6 }}>{type}</div>
            {list.map((it, i) => {
              const sub = (spec.groupBy === 'spec_section' ? it.spec_section : it.room_string) || ''
              const hint = spec.columns[1] ? scheduleCell(it, spec.columns[1][1]) : ''
              return (
                <div key={it.id || i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0', borderBottom: '0.5px solid rgba(7,39,45,0.08)' }}>
                  <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 14, color: '#07272D' }}>{it.item_label || '(unnamed)'}</span>
                  <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#7a9aaa', textAlign: 'right', flexShrink: 0 }}>
                    {[sub, hint].filter(Boolean).join(' · ')}
                  </span>
                </div>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}

export default function ScheduleApprovalEntry({ entry, response, onRespond, viewOnly }) {
  const [mode, setMode] = useState(null) // null | 'changes'
  const [note, setNote] = useState('')

  const sched = entry.schedule || null
  const decided = response && (response.kind === 'approve' || response.kind === 'request_changes')

  // Not issued yet — nothing to approve.
  if (!sched || !sched.release) {
    return (
      <div>
        <div style={J.eyebrow}>Schedule</div>
        <h2 style={J.title}>{entry.title || 'Schedule for your approval'}</h2>
        <hr style={J.rule} />
        <div style={{ ...J.body, fontStyle: 'italic', color: '#7a9aaa' }}>This schedule is being prepared. We'll let you know the moment it's ready for your review.</div>
      </div>
    )
  }

  const header = (
    <>
      <div style={J.eyebrow}>Schedule</div>
      <h2 style={J.title}>{entry.title || ((sched.release.milestone_name || 'Schedule') + ' — for your approval')}</h2>
      <hr style={J.rule} />
      {sched.release.issued_date && (
        <div style={{ fontFamily: '"DM Mono",monospace', fontSize: 11, color: '#7a9aaa', marginBottom: 14 }}>
          Issued {sched.release.issued_date} · {sched.release.item_count} item{sched.release.item_count !== 1 ? 's' : ''}
        </div>
      )}
      {entry.data?.intro && <div style={{ ...J.body, marginBottom: 18 }}><Markdown text={entry.data.intro} /></div>}
      <ScheduleSummary items={sched.items} />
    </>
  )

  if (decided) {
    const approved = response.kind === 'approve'
    return (
      <div>
        {header}
        <div style={{ marginTop: 4 }}>
          <DoneChip>{approved ? 'Approved' : 'Changes requested'}</DoneChip>
          {response.payload?.note && (
            <div style={{ ...J.body, marginTop: 14, fontStyle: 'italic' }}>&ldquo;{response.payload.note}&rdquo;</div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div>
      {header}
      {viewOnly ? <ViewOnlyNote /> : mode === 'changes' ? (
        <div>
          <textarea
            value={note} onChange={e => setNote(e.target.value)} autoFocus
            placeholder="What would you like changed?"
            style={{
              width: '100%', minHeight: 90, padding: 12, borderRadius: 10, resize: 'vertical',
              border: '0.5px solid rgba(7,39,45,0.25)', fontFamily: '"DM Sans", sans-serif',
              fontSize: 14, color: '#07272D', outline: 'none', boxSizing: 'border-box',
            }} />
          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            <PrimaryButton disabled={!note.trim()} onClick={() => onRespond('request_changes', { note: note.trim() })}>
              Send request
            </PrimaryButton>
            <GhostButton onClick={() => { setMode(null); setNote('') }}>Cancel</GhostButton>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 10 }}>
          <PrimaryButton onClick={() => onRespond('approve')}>Approve schedule</PrimaryButton>
          <GhostButton onClick={() => setMode('changes')}>Request changes</GhostButton>
        </div>
      )}
    </div>
  )
}
